function createElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function appendChildren(root, children) {
  root.append(...children.filter(Boolean));
  return root;
}

function createBadge(text, active = false) {
  return createElement('span', active ? 'badge badge-active' : 'badge', text);
}

function createFieldFactory({ getSetting, getLabel, getHelp, getAllowedValues, normalizeControlValue = (_, value) => value, valueLabel }) {
  function createControl(key, options = {}) {
    const item = getSetting(key);
    if (!item) return null;
    const type = options.type || item.type || 'string';
    const allowed = getAllowedValues(key, item, options);
    let control;
    if (type === 'boolean') {
      const toggle = createElement('label', 'provider-toggle');
      control = document.createElement('input');
      control.type = 'checkbox';
      control.checked = item.value === true;
      toggle.append(control, document.createElement('span'));
      control.dataset.providerKey = key;
      control.dataset.providerType = type;
      return toggle;
    }
    if (Array.isArray(allowed)) {
      control = createElement('select', 'provider-input');
      const current = String(normalizeControlValue(key, item.value) ?? '');
      allowed.forEach((value) => {
        const option = createElement('option', '', valueLabel(key, value));
        option.value = String(value);
        option.selected = current === String(value);
        control.append(option);
      });
    } else {
      control = createElement('input', 'provider-input');
      control.type = type === 'number' ? 'number' : 'text';
      control.value = String(item.value ?? '');
      Object.entries(options).forEach(([name, value]) => {
        if (['type', 'label'].includes(name) || value == null) return;
        control.setAttribute(name, String(value));
      });
    }
    control.dataset.providerKey = key;
    control.dataset.providerType = type;
    return control;
  }

  return function createField(key, options = {}) {
    const item = getSetting(key);
    if (!item) return null;
    const field = createElement('div', 'provider-field');
    const head = createElement('span', 'provider-field-head');
    head.append(
      createElement('span', '', options.label || getLabel(key) || key),
      createBadge(item.source === 'runtime' ? '已修改' : '默认', item.source === 'runtime'),
    );
    field.append(head, createControl(key, options), createElement('small', '', getHelp(key) || item.description || ''));
    return field;
  };
}

function createReadonlyField(label, value, hint, badge = '当前') {
  const field = createElement('div', 'provider-field');
  const head = createElement('span', 'provider-field-head');
  head.append(createElement('span', '', label), createBadge(badge, true));
  field.append(head, createElement('div', 'provider-readonly', value), createElement('small', '', hint));
  return field;
}

function createCard({ title, icon, primary = false, children = [] }) {
  const card = createElement('section', `provider-card${primary ? ' provider-card-primary' : ''}`);
  const heading = createElement('div', 'provider-card-title');
  heading.append(createElement('span', 'card-icon', icon), document.createTextNode(title));
  return appendChildren(card, [heading, ...children]);
}

function createCardHead(eyebrow, title, badge = null, className = '') {
  const head = createElement('div', `provider-card-head${className ? ` ${className}` : ''}`);
  const copy = document.createElement('div');
  copy.append(createElement('p', '', eyebrow), createElement('h3', '', title));
  head.append(copy);
  if (badge) head.append(badge);
  return head;
}

function createGrid(className, children) {
  return appendChildren(createElement('div', className), children);
}

function createLlmCard(field, valueLabel) {
  const currentLabel = valueLabel('llm.provider', 'minimax') || '未设置';
  return createCard({
    title: '文本模型', icon: '🤖', primary: true,
    children: [
      createCardHead('当前模型', currentLabel, (() => {
        const badge = createElement('span', 'provider-badge-active');
        badge.append(createElement('span', 'dot'), document.createTextNode(currentLabel));
        return badge;
      })()),
      createGrid('provider-field-grid provider-field-grid-main', [
        createReadonlyField('文本 Provider', 'MiniMax', '生产新请求固定使用 MiniMax；历史 Gemini 用量仅保留读取兼容。'),
        createReadonlyField('并发协调', 'Redis 全局调度', '应用进程不再设置独立 AI 并发上限；所有 MiniMax HTTP 请求共享 Redis 容量。'),
      ]),
      createGrid('provider-subgrid', [
        field('llm.minimax.generationModel'), field('llm.minimax.reflectionModel'),
        field('llm.minimax.rpmLimit', { type: 'number', min: 1, step: 50 }),
        field('llm.minimax.tpmLimit', { type: 'number', min: 1000, step: 1000000 }),
      ]),
    ],
  });
}

function createAichatCard(field, getSetting, valueLabel) {
  const model = String(getSetting('llm.minimax.agentModel')?.value || 'MiniMax-M3');
  const label = valueLabel('llm.minimax.agentModel', model) || model;
  const badge = createElement('span', 'provider-badge-active');
  badge.append(createElement('span', 'dot'), document.createTextNode(label));
  return createCard({ title: 'AI 对话', icon: '💬', children: [
    createCardHead('Agent 执行模型', label, badge),
    createGrid('provider-field-grid provider-field-grid-main', [
      createReadonlyField('会话 Provider', 'MiniMax', '新会话和历史会话的新回复统一使用 MiniMax。'),
      field('llm.minimax.agentModel'), field('aichat.model'),
    ]),
    createGrid('provider-subgrid', [
      field('aichat.attachments.enabled', { type: 'boolean' }),
      createReadonlyField('联网与工具总入口', '始终开启', '这是 AI Chat 的核心能力。具体业务工具仍由技能声明与单工具策略决定。'),
      field('aichat.tools.maxRounds', { type: 'number', min: 1, max: 20, step: 1 }),
    ]),
    createGrid('provider-subgrid', [
      createReadonlyField('联网搜索', '始终开启', '包含网络检索与网页读取，不提供关闭操作。'),
      field('aichat.tools.amazon_listing_fetch.enabled', { type: 'boolean' }),
      field('aichat.tools.asin_lookup.enabled', { type: 'boolean' }),
      field('aichat.tools.compliance_check.enabled', { type: 'boolean' }),
      field('aichat.tools.listing_score.enabled', { type: 'boolean' }),
    ]),
  ] });
}

function createAdOptimizationCard(field, getSetting, valueLabel) {
  const provider = String(getSetting('adOptimization.erpProvider')?.value || 'legacy_selectdb');
  const mode = String(getSetting('adOptimization.operatingMode')?.value || 'shadow');
  return createCard({ title: '广告优化 ERP 数据源', icon: '📈', children: [
    createCardHead('当前分析链路', valueLabel('adOptimization.erpProvider', provider), createElement('span', mode === 'live' ? 'provider-badge-active' : 'provider-badge-muted', valueLabel('adOptimization.operatingMode', mode))),
    createGrid('provider-field-grid', [
      field('adOptimization.erpProvider'), field('adOptimization.operatingMode'), field('adOptimization.shadowAutomationEnabled', { type: 'boolean' }), field('adOptimization.shadowDailySkuLimit', { type: 'number', min: 1, max: 200, step: 1 }),
    ]),
    createGrid('provider-subgrid', [
      field('adOptimization.shadowMinimumSamples', { type: 'number', min: 5, step: 1 }), field('adOptimization.shadowMinimumSupportRate', { type: 'number', min: 0.5, max: 1, step: 0.01 }), field('adOptimization.shadowMinimumDistinctSkus', { type: 'number', min: 2, step: 1 }), field('adOptimization.shadowMinimumDistinctBaselineDates', { type: 'number', min: 2, step: 1 }),
    ]),
    createElement('div', 'provider-info', 'LIVE 模式只能在规则达到 14 天、样本量、独立 SKU、跨日期和支持率门槛，并由管理员迁移为正式规则后启用。选择“领星”不会绕过这些规则门槛；外部执行仍需领星授权与执行接口就绪。'),
  ] });
}

function createImageCard(field, getSetting, valueLabel) {
  const provider = getSetting('linkfox.suite.provider')?.value || 'BANANA_PRO';
  const resolution = getSetting('linkfox.suite.resolution')?.value || '2K';
  const active = createElement('span', 'provider-badge-active');
  active.append(createElement('span', 'dot'), document.createTextNode('LinkFox（紫鸟）'));
  const suite = createElement('div', 'provider-suite-item');
  suite.append(active, createElement('span', 'suite-note', '当前主链路'));
  return createCard({ title: '图片生成', icon: '🖼️', children: [
    appendChildren(createElement('div', 'provider-suite-row'), [suite]),
    createCardHead('LinkFox 商品套图', 'Step7 默认参数', createElement('span', 'provider-status', '当前生图主链路')),
    createGrid('provider-field-grid', [
      createReadonlyField('当前商品套图链路', 'LinkFox（紫鸟）', 'Step7 商品套图当前通过 LinkFox 提交。下面的默认值会写入用户新打开的 Step7 表单。'),
      createReadonlyField('新任务默认组合', `${valueLabel('linkfox.suite.provider', provider)} / ${resolution}`, '管理员保存后，新进入 Step7 的商品套图任务会默认使用这组参数。', '默认'),
      field('linkfox.suite.provider'), field('linkfox.suite.resolution'), field('linkfox.suite.extractPrePoint'),
    ]),
    createCardHead('LinkFox V2', '尺寸图 / 智能修图默认值', null, 'provider-card-head--spaced'),
    createGrid('provider-field-grid', [
      field('linkfox.imageEditV2.provider'), field('linkfox.imageEditV2.outputNum', { type: 'number', min: 1, max: 4, step: 1 }), field('linkfox.imageEditV2.resolution'), field('linkfox.imageEditV2.supplyType'),
    ]),
    createElement('div', 'provider-info', '图片生成统一通过 LinkFox 紫鸟；BANANA_PRO、GPT_2_IMAGE 等名称是紫鸟聚合平台的现役模型选项。'),
  ] });
}

function createExternalCard(field) {
  return createCard({ title: '外部服务连接', icon: '🔭', children: [
    createCardHead('市场数据与 LinkFox', '数据源 / 轮询参数'),
    createGrid('provider-field-grid', [
      field('marketData.provider'), field('linkfox.pollIntervalMs', { type: 'number', min: 2000, step: 1000 }), field('linkfox.pollTimeoutMs', { type: 'number', min: 60000, step: 30000 }), field('linkfox.requestTimeoutMs', { type: 'number', min: 5000, step: 5000 }),
    ]),
  ] });
}

export function createProviderSettingsView(deps) {
  const field = createFieldFactory(deps);
  const row = document.createElement('tr');
  const cell = createElement('td', 'provider-settings-cell');
  cell.colSpan = 5;
  const shell = createElement('div', 'provider-settings-shell');
  shell.append(
    createLlmCard(field, deps.valueLabel),
    createAichatCard(field, deps.getSetting, deps.valueLabel),
    createAdOptimizationCard(field, deps.getSetting, deps.valueLabel),
    createImageCard(field, deps.getSetting, deps.valueLabel),
    createExternalCard(field),
  );
  cell.append(shell);
  row.append(cell);
  return row;
}
